// qs-store-gallery.ts
import type { BrushPreset, ColorSwatch, Layer, TextObject, Tool } from '@/types/qs-types-index'
import { useLayerStore } from '@/stores/qs-store-layers'
import { useStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export interface DrawingCanvasState {
  scale: number
  position: { x: number; y: number }
  backgroundColor: string
}

export interface DrawingToolState {
  currentTool: Tool
  backgroundColor: string
  colorSwatches: ColorSwatch[]
  brushPresets: BrushPreset[]
}

export interface Drawing {
  id: string
  name: string
  createdAt: Date | string
  updatedAt: Date | string
  layers: Layer[]
  currentLayerId?: string
  canvasState: DrawingCanvasState
  toolState: DrawingToolState
  textObjects?: TextObject[]
}

export const useGalleryStore = defineStore('gallery', () => {
  const MAX_DRAWINGS = 100

  const drawings = useStorage<Drawing[]>('vectorsketch-gallery', [])
  const currentDrawingId = useStorage<string | null>('vectorsketch-current-drawing', null)
  const drawingCounter = useStorage('vectorsketch-drawing-counter', 0)
  const isSidebarOpen = ref(false)

  const currentDrawing = computed(() =>
    drawings.value.find(drawing => drawing.id === currentDrawingId.value)
  )

  const drawingsSorted = computed(() =>
    [...drawings.value].sort((a, b) => {
      const aDate = a.updatedAt instanceof Date ? a.updatedAt : new Date(a.updatedAt)
      const bDate = b.updatedAt instanceof Date ? b.updatedAt : new Date(b.updatedAt)
      return bDate.getTime() - aDate.getTime()
    })
  )

  const generateUniqueDrawingId = () => {
    const timestamp = Date.now()
    const random = Math.random().toString(36).substring(2, 11)
    return `drawing-${timestamp}-${random}`
  }

  const enforceMaxDrawings = () => {
    if (drawings.value.length > MAX_DRAWINGS) {
      const sorted = [...drawings.value].sort((a, b) => {
        const aDate = a.updatedAt instanceof Date ? a.updatedAt : new Date(a.updatedAt)
        const bDate = b.updatedAt instanceof Date ? b.updatedAt : new Date(b.updatedAt)
        return bDate.getTime() - aDate.getTime()
      })
      drawings.value = sorted.slice(0, MAX_DRAWINGS)
      if (currentDrawingId.value && !drawings.value.find(d => d.id === currentDrawingId.value)) {
        currentDrawingId.value = drawings.value[0]?.id || null
      }
    }
  }

  const createNewDrawing = (name?: string, backgroundColor?: string): Drawing => {
    const currentBackgroundColor = backgroundColor || '#ebbcbc'
    const newLayerId = `layer-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
    const newLayer = {
      id: newLayerId,
      name: 'L1',
      visible: true,
      locked: false,
      opacity: 1,
      order: 1,
      paths: [],
      position: { x: 0, y: 0 },
      createdAt: new Date(),
      updatedAt: new Date()
    }

    const newDrawing: Drawing = {
      id: generateUniqueDrawingId(),
      name: name || `Drawing ${++drawingCounter.value}`,
      createdAt: new Date(),
      updatedAt: new Date(),
      layers: [newLayer],
      currentLayerId: newLayerId,
      canvasState: {
        scale: 1,
        position: { x: 0, y: 0 },
        backgroundColor: currentBackgroundColor
      },
      toolState: {
        currentTool: {
          type: 'brush',
          size: 2,
          color: '#3498db',
          opacity: 1,
          brushType: 'round'
        },
        backgroundColor: currentBackgroundColor,
        colorSwatches: [
          { id: 'swatch-1', color: '#000000', name: 'Black' },
          { id: 'swatch-2', color: '#ff0000', name: 'Red' },
          { id: 'swatch-3', color: '#00ff00', name: 'Green' },
          { id: 'swatch-4', color: '#0000ff', name: 'Blue' },
          { id: 'swatch-5', color: '#ffff00', name: 'Yellow' },
          { id: 'swatch-6', color: '#ff00ff', name: 'Magenta' }
        ],
        brushPresets: [
          { id: 'brush-1', size: 2, name: 'Fine' },
          { id: 'brush-2', size: 4, name: 'Medium' },
          { id: 'brush-3', size: 8, name: 'Thick' }
        ]
      },
      textObjects: []
    }

    drawings.value.push(newDrawing)
    currentDrawingId.value = newDrawing.id
    enforceMaxDrawings()

    return newDrawing
  }

  const saveCurrentDrawing = (layers: Layer[], canvasState: DrawingCanvasState, toolState: DrawingToolState, textObjects?: TextObject[]) => {
    const drawing = currentDrawing.value
    if (!drawing) return

    const layerStore = useLayerStore()

    const newState = {
      layers,
      currentLayerId: layerStore.currentLayerId || undefined,
      canvasState,
      toolState,
      textObjects: textObjects || []
    }

    const currentState = JSON.stringify({
      layers: drawing.layers,
      currentLayerId: drawing.currentLayerId,
      canvasState: drawing.canvasState,
      toolState: drawing.toolState,
      textObjects: drawing.textObjects
    })

    const newStateString = JSON.stringify(newState)

    if (currentState === newStateString) {
      return
    }

    drawing.layers = layers
    drawing.currentLayerId = newState.currentLayerId
    drawing.canvasState = canvasState
    drawing.toolState = toolState
    drawing.textObjects = newState.textObjects
    drawing.updatedAt = new Date()

    drawings.value = [...drawings.value]
  }

  const loadDrawing = (drawingId: string): Drawing | null => {
    const drawing = drawings.value.find(d => d.id === drawingId)
    if (drawing) {
      currentDrawingId.value = drawingId
      return drawing
    }
    return null
  }

  const duplicateDrawing = (drawingId: string): Drawing | null => {
    const originalDrawing = drawings.value.find(d => d.id === drawingId)
    if (!originalDrawing) return null

    const generateUniquePathId = () => {
      const timestamp = Date.now()
      const random = Math.random().toString(36).substring(2, 11)
      return `path-${timestamp}-${random}`
    }

    const duplicatedDrawing: Drawing = {
      id: generateUniqueDrawingId(),
      name: `${originalDrawing.name} (Copy)`,
      createdAt: new Date(),
      updatedAt: new Date(),
      layers: originalDrawing.layers.map(layer => {
        const newLayerId = `layer-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
        return {
          ...layer,
          id: newLayerId,
          createdAt: new Date(layer.createdAt),
          updatedAt: new Date(layer.updatedAt),
          paths: layer.paths.map(path => ({
            ...path,
            id: generateUniquePathId(),
            layerId: newLayerId
          }))
        }
      }),
      currentLayerId: originalDrawing.currentLayerId,
      canvasState: { ...originalDrawing.canvasState },
      toolState: {
        ...originalDrawing.toolState,
        colorSwatches: originalDrawing.toolState.colorSwatches.map(s => ({ ...s })),
        brushPresets: originalDrawing.toolState.brushPresets.map(p => ({ ...p }))
      }
    }

    duplicatedDrawing.currentLayerId = duplicatedDrawing.layers[0]?.id

    drawings.value.push(duplicatedDrawing)
    currentDrawingId.value = duplicatedDrawing.id
    enforceMaxDrawings()

    return duplicatedDrawing
  }

  const deleteDrawing = (drawingId: string) => {
    const index = drawings.value.findIndex(d => d.id === drawingId)
    if (index !== -1) {
      drawings.value = drawings.value.filter(d => d.id !== drawingId)

      if (currentDrawingId.value === drawingId) {
        if (drawings.value.length > 0) {
          currentDrawingId.value = drawings.value[0]?.id || null
        } else {
          createNewDrawing()
        }
      }
    }
  }

  const renameDrawing = (drawingId: string, newName: string) => {
    const drawing = drawings.value.find(d => d.id === drawingId)
    if (drawing) {
      drawing.name = newName
      drawing.updatedAt = new Date()
      drawings.value = [...drawings.value]
    }
  }

  const toggleSidebar = () => {
    isSidebarOpen.value = !isSidebarOpen.value
  }

  const closeSidebar = () => {
    isSidebarOpen.value = false
  }

  const clearAllDrawings = () => {
    drawings.value = []
    currentDrawingId.value = null
    drawingCounter.value = 0
    createNewDrawing()
  }

  const cleanup = () => {
    isSidebarOpen.value = false
  }

  return {
    drawings,
    currentDrawingId,
    isSidebarOpen,
    drawingCounter,
    currentDrawing,
    drawingsSorted,
    createNewDrawing,
    saveCurrentDrawing,
    loadDrawing,
    duplicateDrawing,
    deleteDrawing,
    renameDrawing,
    toggleSidebar,
    closeSidebar,
    clearAllDrawings,
    cleanup
  }
})